import { useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { Check, Lock } from '@plataforma/ui/iconos';
import type { Sitio } from './comercial-comun';
import { VistaPrevia } from './VistaPrevia';

interface PlantillaDelLotero {
  clave: string;
  nombre: string;
  descripcion: string;
  incluida: boolean;
  recomendada: boolean;
  enPlanes: { clave: string; nombre: string }[];
}
interface Respuesta {
  plan: string | null;
  tipos: string[];
  respaldo: string;
  plantillas: PlantillaDelLotero[];
}

/** Colores de cada plantilla (los de plantillas.css de la tienda), para reconocerlas de un vistazo. */
const PALETA: Record<string, string[]> = {
  taller: ['#fbf8f4', '#b4441c', '#211b16'],
  escaparate: ['#15120e', '#d6ad55', '#f1ead9'],
  catalogo: ['#f6f7f9', '#c62828', '#131824'],
  yonke: ['#16191e', '#f5c400', '#ece9e2'],
  agencia: ['#fbfbf8', '#1f5c46', '#141a17'],
  barrio: ['#fbf5e6', '#c2185b', '#f1d277'],
  subasta: ['#14213d', '#4ade80', '#f4f6fa'],
};

const TIPO: Record<string, string> = {
  reparador: 'reparador',
  seminuevos: 'de seminuevos',
  yonke: 'yonke',
  proveedor: 'proveedor',
};

/**
 * Admin › Mi sitio › Plantilla (arquitectura 2.19.2, I4; pantalla H aprobada): primero las
 * recomendadas para su tipo de negocio, después las demás de su plan y al final las de otros
 * planes con candado («Mejorar a …»). La vista previa usa su tienda real.
 */
export function ElegirPlantilla({
  sitio,
  dominio,
  alCambiar,
}: {
  sitio: Sitio;
  dominio: string | undefined;
  alCambiar: () => void;
}) {
  const api = useApi();
  const consulta = useConsulta<Respuesta>('/platform/mi-lotero/plantillas');
  const efectiva = sitio.plantillaEfectiva ?? sitio.plantilla ?? 'taller';
  const [vista, setVista] = useState<string>(efectiva);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const d = consulta.datos;
  if (consulta.error) return <p className="error">{consulta.error}</p>;
  if (!d) return <p className="subtitulo">Cargando plantillas…</p>;

  const lista = d.plantillas;
  const grupos = [
    {
      titulo: `Recomendadas para un negocio ${d.tipos.map((t) => TIPO[t] ?? t).join(' y ')}`,
      texto: 'Pensadas para lo que vendes y cómo te buscan tus clientes.',
      items: lista.filter((p) => p.incluida && p.recomendada),
    },
    {
      titulo: 'También incluidas en tu plan',
      texto: 'Puedes usar cualquiera de estas sin costo extra.',
      items: lista.filter((p) => p.incluida && !p.recomendada),
    },
    {
      titulo: 'En otros planes',
      texto: 'Puedes verlas con tus datos; para usarlas, mejora tu plan.',
      items: lista.filter((p) => !p.incluida),
    },
  ].filter((g) => g.items.length);
  const elegida = lista.find((p) => p.clave === vista) ?? lista[0];
  const respaldo = lista.find((p) => p.clave === d.respaldo);

  const usar = async (clave: string) => {
    setError(null);
    setGuardando(true);
    try {
      await api('PUT', '/commerce/sitio/plantilla', { plantilla: clave });
      setVersion((v) => v + 1);
      alCambiar();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section
      className="seccion cuerpo elegir-plantilla"
      id="plantilla"
      aria-labelledby="t-plantilla"
    >
      <h2 id="t-plantilla">Cómo se ve tu tienda</h2>
      <p className="aviso-plantilla">
        Cambiar de plantilla no toca tus datos, tus fotos ni tu inventario: es la misma tienda con
        otra presentación y puedes volver cuando quieras.
        {respaldo && (
          <>
            {' '}
            Si tu plan deja de incluir la plantilla que usas, tu tienda pasa sola a{' '}
            <b>{respaldo.nombre}</b>.
          </>
        )}
      </p>
      {error && <p className="error">{error}</p>}
      <div className="elegir-cuerpo">
        <div>
          {grupos.map((g) => (
            <div key={g.titulo} className="grupo-plantillas">
              <h3>{g.titulo}</h3>
              <p className="subtitulo">{g.texto}</p>
              <div className="rejilla-plantillas">
                {g.items.map((p) => {
                  const enUso = p.clave === efectiva;
                  const plan = p.enPlanes[0];
                  return (
                    <button
                      key={p.clave}
                      type="button"
                      className="opcion-plantilla"
                      aria-pressed={vista === p.clave}
                      data-bloqueada={!p.incluida}
                      onClick={() => setVista(p.clave)}
                    >
                      <span className="muestra" aria-hidden="true">
                        {(PALETA[p.clave] ?? []).map((c) => (
                          <i key={c} style={{ background: c }} />
                        ))}
                      </span>
                      <b>{p.nombre}</b>
                      <small>
                        {p.incluida
                          ? p.descripcion
                          : plan
                            ? `Disponible en el plan ${plan.nombre}`
                            : 'No disponible'}
                      </small>
                      {enUso ? (
                        <span className="etiqueta exito">
                          <Check size={14} aria-hidden="true" /> En uso
                        </span>
                      ) : !p.incluida ? (
                        <span className="etiqueta">
                          <Lock size={14} aria-hidden="true" />{' '}
                          {plan ? `Plan ${plan.nombre}` : 'Bloqueada'}
                        </span>
                      ) : p.recomendada ? (
                        <span className="etiqueta aviso">Recomendada</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {elegida && (
          <aside className="elegir-vista">
            <VistaPrevia
              dominio={dominio}
              plantilla={elegida.clave}
              nombre={elegida.nombre}
              version={version}
            />
            <p className="subtitulo">{elegida.descripcion}</p>
            <div className="acciones-plantilla">
              {elegida.clave === efectiva ? (
                <span className="etiqueta exito">
                  <Check size={14} aria-hidden="true" /> Es la que usa tu tienda
                </span>
              ) : elegida.incluida ? (
                <button
                  type="button"
                  className="boton primario"
                  disabled={guardando}
                  onClick={() => void usar(elegida.clave)}
                >
                  {guardando ? 'Guardando…' : `Usar ${elegida.nombre}`}
                </button>
              ) : (
                <Link className="boton primario" to="/suscripcion">
                  <Lock size={16} aria-hidden="true" /> Mejorar a{' '}
                  {elegida.enPlanes[0]?.nombre ?? 'otro plan'}
                </Link>
              )}
            </div>
          </aside>
        )}
      </div>
    </section>
  );
}
